import { Box, Divider, Link, Typography } from "@mui/material";
import { BankConnectionMetaImportProgressEnum } from "@rentablo/api/apiv3";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@rentablo/components";
import { useBankImportContext } from "@providers/BankImportStatusProvider/BankImportStatusProvider.tsx";
import AccountPreviewIcon from "@components/Accounts/AccountPreviewCard/AccountPreviewIcon.tsx";
import { getRedirectType } from "@components/Accounts/utils.ts";
import { StatusMessage, StatusPopup } from "@components/StatusPopup";

const ErrorMessage = () => {
  const { closeMessage, type } = useBankImportContext();
  const { t } = useTranslation();

  return (
    <StatusMessage
      variant="error"
      title={t(
        `bankImportStatus.error${type === "import" ? "Import" : "Update"}`
      )}
      onClose={closeMessage}
      data-testid={`bank-status-message-error-${type}`}
    />
  );
};

const SuccessMessage = () => {
  const { closeMessage, accounts, type } = useBankImportContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <StatusMessage
      variant="success"
      title={t(
        `bankImportStatus.success${type === "update" ? "Update" : "Import"}`
      )}
      onClose={closeMessage}
      collapsible={Boolean(accounts?.length)}
      data-testid={`bank-status-message-success-${type}`}
    >
      <Box maxHeight="300px" overflow="auto" pt={3} px={1}>
        <Typography
          variant="caption"
          color="textSecondary"
          textTransform="uppercase"
        >
          {t(
            `bankImportStatus.${
              type === "import" ? "importedData" : "updatedData"
            }`
          )}
        </Typography>
        {accounts?.map((account, index) => {
          const redirectType = getRedirectType(account);
          return (
            <Box
              pt={3}
              pb={index === accounts.length - 1 ? 1 : 3}
              key={account.id}
            >
              <Box
                key={account.id}
                display="flex"
                gap={2.5}
                alignItems="center"
              >
                <AccountPreviewIcon
                  isReference={Boolean(account.depotIds?.length)}
                  iconUrl={account.iconUrl}
                  type={account.type}
                  size={24}
                />
                <Link
                  onClick={() => navigate(`/${redirectType}/${account.id}`)}
                  variant="caption"
                  color="primary.main"
                >
                  {account.name}
                </Link>
              </Box>
              {account.referenceAccount && (
                <Box
                  key={account.id}
                  display="flex"
                  gap={1.5}
                  pt={1}
                  pl={1}
                  alignItems="center"
                >
                  <AccountPreviewIcon
                    isReference
                    iconUrl={account.referenceAccount.iconUrl}
                    type={account.referenceAccount.type}
                    size={24}
                  />
                  <Link
                    onClick={() =>
                      navigate(`/accounts/${account.referenceAccount?.id}`)
                    }
                    variant="caption"
                    color="primary.main"
                  >
                    {account.referenceAccount.name}
                  </Link>
                </Box>
              )}
              {index < accounts.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Box>
    </StatusMessage>
  );
};

const InProgress = ({ progress }: { progress: number }) => {
  const { status } = useBankImportContext();
  const { t } = useTranslation();
  return (
    <Box display="flex" justifyContent="space-between" gap={3} px={2} py={1.75}>
      <Box display="flex" gap={2.5} alignItems="center">
        <LoadingSpinner width={24} />
        <Typography variant="body3" color="textSecondary">
          {progress}%
        </Typography>
        <Typography variant="body3">
          {t(`bankImportStatus.status.${status}`)}
        </Typography>
      </Box>
    </Box>
  );
};

export const BankStatusMessage = () => {
  const { status } = useBankImportContext();

  switch (status) {
    case BankConnectionMetaImportProgressEnum.CONNECTING:
      return <InProgress progress={10} />;
    case BankConnectionMetaImportProgressEnum.ACCOUNTS:
      return <InProgress progress={20} />;
    case BankConnectionMetaImportProgressEnum.TRANSACTIONS:
      return <InProgress progress={40} />;
    case BankConnectionMetaImportProgressEnum.PERSISTING_TRANSACTIONS:
      return <InProgress progress={50} />;
    case BankConnectionMetaImportProgressEnum.PERSISTING_BOOKINGS:
      return <InProgress progress={60} />;
    case BankConnectionMetaImportProgressEnum.SECURITIES:
      return <InProgress progress={80} />;
    case BankConnectionMetaImportProgressEnum.SUCCESS:
      return <SuccessMessage />;
    case BankConnectionMetaImportProgressEnum.FAILED_WITH_ERROR:
      return <ErrorMessage />;
    default:
      return null;
  }
};

export const BankStatus = () => {
  const { status } = useBankImportContext();

  return (
    <StatusPopup in={!!status} data-testId="bank-status-message">
      <BankStatusMessage />
    </StatusPopup>
  );
};
